export const TERMINAL_JOURNEY_STATUSES = [
  'Completed',
  'Cancelled',
  'No Show',
  'Aborted',
]

// The status timestamps operational staff care about, in workflow order.
// PTS wording (not event control): the patient is collected and handed over.
export const JOURNEY_TIME_MILESTONES = [
  { status: 'Mobile', label: 'Mobile' },
  { status: 'On Scene', label: 'On Scene' },
  { status: 'Patient Onboard', label: 'With Patient' },
  { status: 'Arrived', label: 'Arrived' },
  { status: 'Handover Complete', label: 'Handover' },
  { status: 'Completed', label: 'Complete' },
]

// statusHistory can contain repeats (e.g. a status re-set after a correction);
// the most recent entry per status wins.
export const getJourneyStatusTimes = (journey) => {
  const times = {}
  ;(journey?.statusHistory || []).forEach((entry) => {
    if (entry?.status && entry?.at) {
      times[entry.status] = entry.at
    }
  })
  return times
}

export const JOURNEY_WORKFLOW_STEPS = [
  {
    statuses: ['Planned', 'Assigned'],
    stateLabel: 'Awaiting crew',
    nextStatus: 'Crew Acknowledged',
    actionLabel: 'Acknowledge',
    helperText: 'Confirm this journey has been received.',
  },
  {
    statuses: ['Crew Acknowledged'],
    stateLabel: 'Ready to travel',
    nextStatus: 'Mobile',
    actionLabel: 'Travel to pickup',
    helperText: 'Start travelling to the pickup location.',
  },
  {
    statuses: ['Mobile'],
    stateLabel: 'Travelling to pickup',
    nextStatus: 'On Scene',
    actionLabel: 'Arrived at pickup',
    helperText: 'Mark arrival at the pickup location.',
  },
  {
    statuses: ['On Scene'],
    stateLabel: 'At pickup',
    nextStatus: 'Patient Onboard',
    actionLabel: 'Patient onboard',
    helperText: 'Confirm the patient is on the vehicle.',
  },
  {
    statuses: ['Patient Onboard'],
    stateLabel: 'Patient onboard',
    nextStatus: 'Transporting',
    actionLabel: 'Travel to destination',
    helperText: 'Start travelling to the destination.',
  },
  {
    statuses: ['Transporting'],
    stateLabel: 'Travelling to destination',
    nextStatus: 'Arrived',
    actionLabel: 'Arrived at destination',
    helperText: 'Mark arrival at the destination.',
  },
  {
    statuses: ['Arrived'],
    stateLabel: 'At destination',
    nextStatus: 'Handover Complete',
    actionLabel: 'Complete handover',
    helperText: 'Confirm handover has been completed.',
  },
  {
    statuses: ['Handover Complete'],
    stateLabel: 'Handover complete',
    nextStatus: 'Completed',
    actionLabel: 'Complete journey',
    helperText: 'Close this journey.',
  },
]

const hasOrganisationDestination = (journey) =>
  !!(
    journey?.dropoff?.organisationAsAddress ||
    journey?.dropoff?.wardDepartmentAsAddress ||
    journey?.dropoff?.when?.organisationAsAddress ||
    journey?.dropoff?.when?.wardDepartmentAsAddress
  )

const isPatientHomeDestination = (journey) =>
  journey?.dropoff?.locationType === 'Patient Address' &&
  !hasOrganisationDestination(journey)

// A Risk Assessment journey is a home visit with no patient on board — the
// crew travel out, arrive, complete the assessment, and close the visit.
const RISK_ASSESSMENT_WORKFLOW_STEPS = [
  {
    statuses: ['Planned', 'Assigned'],
    stateLabel: 'Awaiting crew',
    nextStatus: 'Crew Acknowledged',
    actionLabel: 'Acknowledge',
    helperText: 'Confirm this assessment visit has been received.',
  },
  {
    statuses: ['Crew Acknowledged'],
    stateLabel: 'Ready to travel',
    nextStatus: 'Mobile',
    actionLabel: 'Travel to visit',
    helperText: "Start travelling to the patient's home.",
  },
  {
    statuses: ['Mobile'],
    stateLabel: 'Travelling to visit',
    nextStatus: 'On Scene',
    actionLabel: 'Arrived at patient home',
    helperText: "Mark arrival at the patient's home.",
  },
  {
    statuses: ['On Scene'],
    stateLabel: 'On scene - assessing',
    nextStatus: 'Completed',
    actionLabel: 'Complete visit',
    helperText: 'Complete the risk assessment, then close the visit.',
  },
]

export const isRiskAssessmentJourney = (journey) =>
  journey?.journeyType === 'Risk Assessment'

export const getJourneyWorkflow = (status = 'Planned', journey = null) => {
  if (TERMINAL_JOURNEY_STATUSES.includes(status)) {
    return {
      stateLabel: status === 'Completed' ? 'Complete' : status,
      nextStatus: null,
      actionLabel: null,
      helperText: 'No further workflow actions are needed.',
      isTerminal: true,
    }
  }

  if (isRiskAssessmentJourney(journey)) {
    return (
      RISK_ASSESSMENT_WORKFLOW_STEPS.find((step) =>
        step.statuses.includes(status),
      ) || RISK_ASSESSMENT_WORKFLOW_STEPS[0]
    )
  }

  if (status === 'Arrived' && isPatientHomeDestination(journey)) {
    return {
      stateLabel: 'At patient home',
      nextStatus: 'Completed',
      actionLabel: 'Complete journey',
      helperText: 'Close this journey after safely dropping the patient home.',
    }
  }

  return (
    JOURNEY_WORKFLOW_STEPS.find((step) => step.statuses.includes(status)) ||
    JOURNEY_WORKFLOW_STEPS[0]
  )
}
